'use strict'

// Layout, summaries, and export columns for a table view.
// A hidden column is left out of the table and of every export.
// Summaries use the rows that remain after filters and access checks.

const { ruleMatches } = require('./submission-rules')

const SUMMARY_KINDS = ['sum', 'average', 'min', 'max']
const PAGE_SIZE_CHOICES = [10, 25, 50, 100, 250]
const MAX_PAGE_SIZE = 500
const MAX_COLUMN_WIDTH = 2000
const ACTION_KINDS = ['update', 'related', 'email', 'workflow']
const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/

function wholeNumber (value) {

  if (typeof value === 'string' && /^\d+$/.test(value.trim())) value = Number(value.trim())
  if (typeof value !== 'number' || !Number.isInteger(value)) return null

  return value
}

function bounded (value, max) {

  const parsed = wholeNumber(value)

  if (parsed == null || parsed < 1 || parsed > max) return null

  return parsed
}

function flag (value) {

  return value === true || value === 'true'
}

function normalizeTableLayout (input) {

  const source = input && typeof input === 'object' ? input : {}
  let pageSize = bounded(source.pageSize, MAX_PAGE_SIZE)
  const pageSizeMax = bounded(source.pageSizeMax, MAX_PAGE_SIZE)

  if (pageSize && pageSizeMax && pageSize > pageSizeMax) pageSize = pageSizeMax

  return {
    pageSize,
    pageSizeMax,
    wrapHeaders: flag(source.wrapHeaders),
    hideEmptyColumns: flag(source.hideEmptyColumns)
  }
}

function pageSizeChoices (layout) {

  const settings = normalizeTableLayout(layout)
  const max = settings.pageSizeMax || settings.pageSize

  if (!max) return []

  const choices = PAGE_SIZE_CHOICES.filter((size) => size <= max)

  if (settings.pageSize && choices.indexOf(settings.pageSize) < 0 && settings.pageSize <= max) {

    choices.push(settings.pageSize)
  }

  return choices.sort((left, right) => left - right)
}

function clampPageSize (requested, layout) {

  const settings = normalizeTableLayout(layout)
  const choices = pageSizeChoices(settings)

  if (!choices.length) return settings.pageSize

  const parsed = wholeNumber(requested)

  if (parsed != null && choices.indexOf(parsed) >= 0) return parsed

  if (settings.pageSize && choices.indexOf(settings.pageSize) >= 0) return settings.pageSize

  return choices[0]
}

function columnDisplay (field) {

  const summary = field && SUMMARY_KINDS.indexOf(field.summary) >= 0 ? field.summary : ''

  return {
    width: bounded(field && field.columnWidth, MAX_COLUMN_WIDTH),
    emptyText: field && typeof field.emptyText === 'string' ? field.emptyText : '',
    summary,
    hidden: flag(field && field.hidden)
  }
}

function isEmptyCell (value) {

  if (value == null) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0

  return false
}

function columnIsEmpty (rows, uniqueTitle) {

  return (rows || []).every((row) => isEmptyCell(row && row[uniqueTitle]))
}

function shownColumns (components, rows, layout) {

  const settings = normalizeTableLayout(layout)

  return (components || []).filter((column) => {

    if (!column || column.type === 'action') return true
    if (columnDisplay(column).hidden) return false
    if (!settings.hideEmptyColumns || !column.uniqueTitle) return true

    return !columnIsEmpty(rows, column.uniqueTitle)
  })
}

function numericValues (values) {

  const numbers = []

  for (const value of values || []) {

    if (value == null || value === '') continue

    const parsed = typeof value === 'number' ? value : Number(String(value).trim())

    if (Number.isFinite(parsed)) numbers.push(parsed)
  }

  return numbers
}

function summarizeValues (values, kind) {

  const numbers = numericValues(values)

  if (!numbers.length || SUMMARY_KINDS.indexOf(kind) < 0) return null

  if (kind === 'min') return Math.min(...numbers)
  if (kind === 'max') return Math.max(...numbers)

  const total = numbers.reduce((sum, value) => sum + value, 0)

  if (kind === 'sum') return total

  return total / numbers.length
}

function summarizeColumns (rows, components) {

  const summary = {}

  for (const column of components || []) {

    const display = columnDisplay(column)

    if (!display.summary || display.hidden || !column.uniqueTitle) continue
    if (column.type === 'action' || column.type === 'link') continue

    summary[column.uniqueTitle] = summarizeValues((rows || []).map((row) => row && row[column.uniqueTitle]), display.summary)
  }

  return summary
}

function exportableComponents (components) {

  return (components || []).filter((column) => {

    if (!column || column.type === 'action') return false

    return !columnDisplay(column).hidden
  })
}

function normalizeActionValues (values) {

  if (values == null) return []
  if (!Array.isArray(values)) return { error: 'Action values must be a list' }

  const assignments = []

  for (const entry of values) {

    if (!entry || typeof entry !== 'object') return { error: 'Each action value must be an object' }
    if (!IDENTIFIER.test(entry.column || '')) return { error: 'An action value needs a column name' }

    const source = entry.source === 'field' ? 'field' : 'static'

    if (source === 'field' && !IDENTIFIER.test(entry.field || '')) {

      return { error: 'A field action value needs a source column' }
    }

    assignments.push({
      column: entry.column,
      source,
      value: source === 'field' ? '' : (entry.value == null ? '' : String(entry.value)),
      field: source === 'field' ? entry.field : ''
    })
  }

  return assignments
}

function normalizeTableAction (component) {

  if (!component || component.type !== 'action') return { error: 'This column is not an action' }
  if (!IDENTIFIER.test(component.uniqueTitle || '')) return { error: 'An action needs an identifier' }

  const kind = ACTION_KINDS.indexOf(component.actionKind) >= 0 ? component.actionKind : ''

  if (!kind) return { error: 'Choose what the action does' }

  const values = normalizeActionValues(component.actionValues)

  if (values.error) return values

  if ((kind === 'update' || kind === 'related') && !values.length) {

    return { error: 'The action needs at least one value' }
  }

  if (kind === 'related' && !IDENTIFIER.test(component.actionRelationship || '')) {

    return { error: 'The action needs a relationship column' }
  }

  if (kind === 'email' && !IDENTIFIER.test(component.actionAutomation || '')) {

    return { error: 'The action needs an email automation' }
  }

  if (kind === 'workflow' && !IDENTIFIER.test(component.actionWorkflow || '')) {

    return { error: 'The action needs a workflow' }
  }

  return {
    id: component.uniqueTitle,
    label: component.label || component.uniqueTitle,
    kind,
    values,
    relationshipColumn: component.actionRelationship || '',
    relatedMode: component.actionRelatedMode === 'insert' ? 'insert' : 'update',
    automationUniqueTitle: component.actionAutomation || '',
    workflowUniqueTitle: component.actionWorkflow || ''
  }
}

function actionValueMap (action, row) {

  const values = {}

  for (const entry of action.values || []) {

    values[entry.column] = entry.source === 'field' ? (row ? row[entry.field] : '') : entry.value
  }

  return values
}

function tableActionAllowed (submissionRules, row) {

  const rules = submissionRules && submissionRules.rules

  if (!Array.isArray(rules) || !rules.length) return true

  return rules.some((rule) => ruleMatches(rule, row, {}))
}

module.exports = {
  SUMMARY_KINDS,
  PAGE_SIZE_CHOICES,
  ACTION_KINDS,
  normalizeTableLayout,
  pageSizeChoices,
  clampPageSize,
  columnDisplay,
  isEmptyCell,
  shownColumns,
  summarizeValues,
  summarizeColumns,
  exportableComponents,
  normalizeTableAction,
  actionValueMap,
  tableActionAllowed
}
